import {
  GiftItemBrand,
  isThanksPrintBoxSet,
  ThanksPrintConfig,
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import {
  GiftThanksPrintDto,
  GiftThanksPrintGroupDto,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { Member } from '@shared/api/member/member.interface';
import { Gender } from '@shared/types';
import { getReverseType } from '@shared/utils/memberUtils';

/** 감사글 안에서 고인/상중 표현으로 치환되는 토큰 */
export const THANKS_DECEASED_TOKEN = '###';

/** 발신 표현 옵션 (기본: 배상) */
export const THANKS_SENDER_SUFFIXES = ['배상', '드림', '올림'] as const;
export type ThanksSenderSuffix = typeof THANKS_SENDER_SUFFIXES[number];

/** 상주 정보가 없을 때 이름 fallback */
export const THANKS_FALLBACK_NAME = 'OOO';

/**
 * reverseType(고인의 상주에 대한 관계 용어) → 구어체 호칭.
 * 예: 부친→아버지, 모친→어머니, 조부→할아버지, 조모→할머니 ...
 */
const REVERSE_TYPE_TO_WORD: Record<string, string> = {
  부친: '아버지',
  모친: '어머니',
  조부: '할아버지',
  조모: '할머니',
  외조부: '외할아버지',
  외조모: '외할머니',
  시부: '시아버지',
  시모: '시어머니',
  시조부: '시할아버지',
  시조모: '시할머니',
  빙조부: '처조부',
  빙조모: '처조모',
  장인: '장인어른',
  장모: '장모님',
  빙부: '장인어른',
  빙모: '장모님',
  부군: '남편',
  부인: '아내',
  배우자: '배우자',
  형제: '형제',
};

/**
 * ### 표현 생성 — relationType(상주의 고인에 대한 관계) + 고인 성별로 계산.
 * - 관계가 확인되면 "저희 {구어체 호칭} 상중" (예: "저희 아버지 상중")
 * - 관계를 만들 수 없으면 "상중" (저희도 제외)
 * - 상주(relationType 미설정)는 아들(자)로 간주해 부친/모친 계산
 */
export const getThanksDeceasedPhrase = (
  relationType: string | undefined,
  deceasedSex: Gender | undefined,
): string => {
  if (!deceasedSex) return '상중';
  const rel = relationType?.trim() || '자';
  const reverseType = getReverseType(rel, deceasedSex);
  const word = reverseType ? REVERSE_TYPE_TO_WORD[reverseType] ?? reverseType : '';
  return word ? `저희 ${word} 상중` : '상중';
};

/** 상주 이름 (없으면 OOO) */
export const getThanksSenderName = (member?: Pick<Member, 'fullName'> | null): string =>
  member?.fullName?.trim() || THANKS_FALLBACK_NAME;

/** 프리셋의 ### 토큰을 "저희 OOO 상중" 표현으로 치환 */
export const fillThanksMessage = (
  template: string,
  relationType: string | undefined,
  deceasedSex: Gender | undefined,
): string =>
  template
    .split(THANKS_DECEASED_TOKEN)
    .join(getThanksDeceasedPhrase(relationType, deceasedSex));

/** 이름 + 발신표현 합성 ("홍길동 배상") */
export const composeThanksSender = (name: string, suffix: ThanksSenderSuffix): string =>
  `${name.trim() || THANKS_FALLBACK_NAME} ${suffix}`;

/**
 * 지정 문구 비교용 정규화 — 백엔드와 동일한 규칙.
 * 줄바꿈(CRLF→LF)·앞뒤 공백만 관대하게 보고, 내부 들여쓰기는 문구의 일부로 엄격 비교.
 */
export const normalizeThanksPreset = (s: string): string =>
  s.replace(/\r\n/g, '\n').trim();

/**
 * 템플릿의 유효한 지정 문구 세트 (빈 문구 제외).
 * 비어있지 않으면 자유입력 불가 — 이 중 하나만 선택 가능. 빈 배열 = 기존 자유입력.
 */
export const templateMessagePresets = <TImage>(
  template?: ThanksPrintTemplate<TImage> | null,
): string[] =>
  (template?.messagePresets ?? []).filter((p) => normalizeThanksPreset(p).length > 0);

/**
 * 이름 입력에 허용되는 최대 글자수.
 * 서버의 senderNameMaxLength는 전송되는 합성값("홍길동 배상") 전체에 적용되므로,
 * 발신표현(공백 1자 + 표현 2자 = 3자)을 제외한 이름 몫을 돌려준다.
 */
export const thanksSenderNameLimit = <TImage>(
  template?: ThanksPrintTemplate<TImage> | null,
): number | undefined => {
  const max = template?.senderNameMaxLength;
  if (typeof max !== 'number' || max <= 0) return undefined;
  return Math.max(1, max - 3);
};

/** 감사인쇄 가능 브랜드 판정을 위한 최소 주문 라인 형태 */
export interface ThanksPrintLine {
  /** 라인 식별자 — 감사인쇄 대상 상품 수 집계(중복 제거)에 사용 */
  itemId: string;
  giftItemInfo?: {
    giftItemBrand?: GiftItemBrand | string;
    /** 감사카드 사용 여부 오버라이드 (undefined = 브랜드 thanksPrint.enabled 따름) */
    useThanksPrint?: boolean;
    /** 이 상품에서 허용하는 템플릿 _id 목록 (빈 배열/미설정 = 브랜드 전체 허용) */
    thanksPrintTemplates?: string[];
    /** 상품 유형 — 쿠폰형은 배송 건이 없어 감사카드 그룹에서 제외 */
    type?: string;
  } | null;
}

/** 주문 전체의 대표 감사인쇄 설정 (주문당 1건으로 통일) */
export interface OrderThanksPrint {
  brandId: string;
  config: ThanksPrintConfig<Image>;
  /** 감사인쇄가 공통 적용되는 대상 상품 수 */
  eligibleCount: number;
  /**
   * 대상 상품들이 공통으로 허용하는 템플릿 _id 목록 (교집합).
   * undefined = 제한 없음(브랜드 전체 허용)
   */
  allowedTemplateIds?: string[];
}

/**
 * 주문 라인들에서 감사인쇄 가능 브랜드를 모아 대표 1개를 고른다.
 * 단건(바로구매)·다건(장바구니) 결제 어디서 호출해도 동일하게 동작하도록 통일한 로직.
 * - 가능 조건: 브랜드가 populate된 객체 + 인쇄 대상(useThanksPrint ?? thanksPrint.enabled)
 *   + 노출 가능 템플릿(enabled !== false ∩ 상품별 허용 목록 교집합) 1개 이상
 * - 대표 선정: 첫 가능 브랜드 (상품 상세·바로구매 = 상품 1개라 브랜드도 하나)
 * - eligibleCount: 감사인쇄 대상 상품 수(같은 itemId 중복 제외)
 */
export const resolveOrderThanksPrint = (
  lines: ThanksPrintLine[],
): OrderThanksPrint | null => {
  const entries: {
    brandId: string;
    config: ThanksPrintConfig<Image>;
    allowed: Set<string> | null; // 상품별 허용 목록의 교집합 (null = 전체 허용)
    itemIds: Set<string>;
  }[] = [];
  for (const line of lines) {
    const brand = line.giftItemInfo?.giftItemBrand;
    if (!brand || typeof brand !== 'object' || !('thanksPrint' in brand)) continue;
    const config = brand.thanksPrint as ThanksPrintConfig<Image> | undefined;
    if (!config) continue;
    // 상품 오버라이드 우선 (백엔드와 동일: useThanksPrint ?? enabled ?? false)
    if (!(line.giftItemInfo?.useThanksPrint ?? config.enabled)) continue;

    let entry = entries.find((c) => c.brandId === brand._id);
    if (!entry) {
      entry = { brandId: brand._id, config, allowed: null, itemIds: new Set() };
      entries.push(entry);
    }
    entry.itemIds.add(line.itemId);

    // 허용 목록 교집합 — 미지정(빈 배열) 상품은 전체 허용이라 교집합에 영향 없음
    const lineAllowed = (line.giftItemInfo?.thanksPrintTemplates ?? [])
      .map(String)
      .filter(Boolean);
    if (lineAllowed.length > 0) {
      const current = entry.allowed;
      entry.allowed =
        current === null
          ? new Set(lineAllowed)
          : new Set(lineAllowed.filter((id) => current.has(id)));
    }
  }

  // 노출 가능 템플릿(enabled ∩ 허용 교집합)이 1개 이상인 브랜드만 후보
  const candidates = entries.filter((c) =>
    (c.config.templates ?? []).some(
      (t) =>
        t.enabled !== false && (c.allowed === null || (t._id && c.allowed.has(t._id))),
    ),
  );
  if (candidates.length === 0) return null;
  const chosen = candidates[0]!;

  const eligibleItemIds = new Set<string>();
  candidates.forEach((c) => c.itemIds.forEach((id) => eligibleItemIds.add(id)));

  return {
    brandId: chosen.brandId,
    config: chosen.config,
    eligibleCount: eligibleItemIds.size,
    ...(chosen.allowed !== null && { allowedTemplateIds: Array.from(chosen.allowed) }),
  };
};

/**
 * 감사카드 1장 = 같은 디자인을 함께 쓸 수 있는 상품 묶음.
 * 같은 브랜드이면서 허용 디자인이 겹치는 상품끼리 한 장, 안 겹치거나 브랜드가 다르면 따로.
 * 묶음끼리는 쓸 수 있는 디자인이 절대 겹치지 않는다 → 디자인별 감사글 보존이 곧 카드별 보존.
 */
export interface OrderThanksPrintGroup {
  /** React key — 브랜드 + 묶음의 첫 상품 */
  key: string;
  brandId: string;
  config: ThanksPrintConfig<Image>;
  /** 이 카드를 받는 상품(GiftItem._id) — 주문 DTO giftItems 그대로 */
  itemIds: string[];
  /** 이 묶음 상품들이 공통으로 쓸 수 있는 디자인 (사용중 ∩ 상품별 허용, 표시 순서) */
  allowedTemplateIds: string[];
  /**
   * 같은 브랜드 감사카드 상품 중 쓸 수 있는 디자인이 하나도 없어 어느 카드에도 못 들어간 상품이 있다
   * (허용 디자인이 전부 사용중지). 이때 카드 1장이라도 thanksPrint(1장 규칙)로 보내면 서버가 그 상품까지
   * 대조해 400 → 결제 실패하므로 thanksPrints 로 보낸다(구서버는 무시 → 예전처럼 카드 없이).
   */
  hasUncoveredItems: boolean;
}

/**
 * 주문 라인 → 감사카드 묶음들. 서버 thanksPrints 검증(gift-order.thanks-print.ts)과 같은 판정:
 * 배송형 + 감사카드 대상(useThanksPrint ?? brand.enabled) + 쓸 수 있는 디자인이 1개 이상.
 * 묶음은 들어온 순서대로 "같은 브랜드 + 허용 디자인 교집합이 남는" 첫 묶음에 붙이고, 없으면 새 묶음.
 * 모든 상품이 한 디자인을 공유하면 묶음은 1개 — 기존 "주문당 카드 1장"과 같다.
 * 순서는 장바구니 순서 그대로(첫 묶음 = 대표). 최근 디자인 순으로 정렬하면 카드 편집을 여는 순간
 * 순서·"카드 N" 라벨이 바뀌어 버린다.
 */
export const resolveOrderThanksPrintGroups = (
  lines: ThanksPrintLine[],
): OrderThanksPrintGroup[] => {
  const groups: (Omit<OrderThanksPrintGroup, 'key' | 'allowedTemplateIds' | 'hasUncoveredItems'> & {
    allowed: Set<string>;
  })[] = [];
  const seen = new Set<string>();
  const uncoveredBrands = new Set<string>();
  for (const line of lines) {
    if (!line.itemId || seen.has(line.itemId)) continue;
    const info = line.giftItemInfo;
    const brand = info?.giftItemBrand;
    if (!brand || typeof brand !== 'object' || !('thanksPrint' in brand)) continue;
    const config = brand.thanksPrint as ThanksPrintConfig<Image> | undefined;
    if (!config) continue;
    if (info?.type === 'Coupon') continue;
    if (!(info?.useThanksPrint ?? config.enabled)) continue;

    const restrict = (info?.thanksPrintTemplates ?? []).map(String).filter(Boolean);
    const usable = new Set(
      (config.templates ?? [])
        .filter((t) => t.enabled !== false && t._id)
        .map((t) => t._id as string)
        .filter((id) => restrict.length === 0 || restrict.includes(id)),
    );
    if (usable.size === 0) {
      uncoveredBrands.add(brand._id);
      continue;
    }
    seen.add(line.itemId);

    const target = groups.find(
      (g) => g.brandId === brand._id && Array.from(g.allowed).some((id) => usable.has(id)),
    );
    if (target) {
      target.allowed = new Set(Array.from(target.allowed).filter((id) => usable.has(id)));
      target.itemIds.push(line.itemId);
    } else {
      groups.push({ brandId: brand._id, config, itemIds: [line.itemId], allowed: usable });
    }
  }

  return groups.map(({ allowed, ...g }) => ({
    ...g,
    key: `${g.brandId}:${g.itemIds[0]}`,
    hasUncoveredItems: uncoveredBrands.has(g.brandId),
    allowedTemplateIds: sortedTemplates(g.config)
      .map((t) => t._id as string)
      .filter((id) => allowed.has(id)),
  }));
};

/** 주문에 보낼 감사인쇄 선택값 (giftThanksPrintAtom 보존값과 같은 구조) */
export interface ThanksPrintSelectionInput {
  senderName: string;
  senderSuffix: ThanksSenderSuffix;
  /** 디자인(템플릿 _id)별 감사글 — 같은 디자인일 때만 쓴다 */
  messages?: Record<string, string>;
  /** 최근 고른 디자인 (앞이 최신) */
  recentTemplateIds?: string[];
}

/** 표시 순서대로 정렬한 템플릿 */
const sortedTemplates = <TImage>(config: ThanksPrintConfig<TImage>) =>
  (config.templates ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/** 노출 템플릿 (사용중 ∩ 허용 목록, 표시 순서). allowedTemplateIds 미지정/빈 배열 = 제한 없음 */
export const visibleThanksTemplates = <TImage>(
  config: ThanksPrintConfig<TImage>,
  allowedTemplateIds?: string[],
): ThanksPrintTemplate<TImage>[] => {
  const allowed =
    allowedTemplateIds && allowedTemplateIds.length > 0 ? new Set(allowedTemplateIds) : null;
  return sortedTemplates(config).filter(
    (t) => t.enabled !== false && (!allowed || (t._id && allowed.has(t._id))),
  );
};

/**
 * 이 카드(노출 템플릿들)에 쓸 디자인 — 최근 고른 디자인 중 여기서 쓸 수 있는 가장 최근 것,
 * 없으면 첫 디자인. (템플릿 _id는 브랜드를 넘어 고유하므로 브랜드 비교가 필요 없다)
 */
export const pickThanksTemplate = <TImage>(
  templates: ThanksPrintTemplate<TImage>[],
  recentTemplateIds: string[] = [],
): ThanksPrintTemplate<TImage> | undefined => {
  for (const id of recentTemplateIds) {
    const found = templates.find((t) => t._id === id);
    if (found) return found;
  }
  return templates[0];
};

/**
 * 이 디자인으로 인쇄될 감사글 — 그 디자인에 보존된 글만 쓴다(다른 디자인 글은 옮기지 않는다).
 * - 감사글 칸이 없는 스티커: 감사글 없음('') — 이름만 인쇄
 * - 지정 문구 디자인: 보존값이 세트 안이면 그 원본, 아니면 첫 문구 — 단 보존값이 없으면 미작성(undefined)
 * - 자유 입력: 보존값(비어 있으면 미작성)
 * undefined = 이 디자인에는 아직 쓴 적 없음(주문에 안 싣고, 화면은 "작성하기")
 */
export const thanksMessageForTemplate = <TImage>(
  template: ThanksPrintTemplate<TImage>,
  selection: Pick<ThanksPrintSelectionInput, 'messages'> | null,
): string | undefined => {
  if (!selection) return undefined;
  const presets = templateMessagePresets(template);
  if (!isThanksPrintBoxSet(template.messageBox)) return presets[0] ?? '';
  const saved = template._id ? selection.messages?.[template._id]?.trim() : undefined;
  if (!saved) return undefined;
  if (presets.length === 0) return saved;
  return (
    presets.find((p) => normalizeThanksPreset(p) === normalizeThanksPreset(saved)) ??
    presets[0] ??
    ''
  );
};

/**
 * 감사인쇄 선택값 + 카드(브랜드 설정·허용 디자인) → 주문 전송용 thanksPrint DTO.
 * - 디자인: 최근 고른 디자인 중 이 카드에서 쓸 수 있는 것, 없으면 첫 노출 디자인.
 * - 감사글: 그 디자인에 보존된 글만(thanksMessageForTemplate). 미작성이면 undefined → 주문에 안 실음.
 *   스티커(감사글 칸 없음)는 이름만이라 선택값만 있으면 싣는다(기존과 같다).
 * - senderName은 발신표현까지 합성("홍길동 배상")해 미리보기와 동일하게 인쇄되도록 하고,
 *   템플릿 글자수 제한(합성 전체 기준)을 넘지 않게 이름을 잘라 서버 거부를 방지한다.
 */
export const buildThanksPrintDto = (
  selection: ThanksPrintSelectionInput | null,
  config: ThanksPrintConfig<Image>,
  _brandId: string,
  allowedTemplateIds?: string[],
): GiftThanksPrintDto | undefined => {
  if (!selection) return undefined;
  const template = pickThanksTemplate(
    visibleThanksTemplates(config, allowedTemplateIds),
    selection.recentTemplateIds,
  );
  const templateId = template?._id;
  if (!template || !templateId) return undefined;

  const message = thanksMessageForTemplate(template, selection);
  if (message === undefined) return undefined;

  const { name, suffix } = parseThanksSender(selection.senderName, selection.senderSuffix);
  const nameLimit = thanksSenderNameLimit(template);
  const limitedName = nameLimit !== undefined ? name.slice(0, nameLimit) : name;
  return { templateId, message, senderName: composeThanksSender(limitedName, suffix) };
};

/** 주문에 보낼 감사카드 묶음 DTO — 작성 안 된 카드는 빠진다 */
const buildThanksPrintGroupDtos = (
  selection: ThanksPrintSelectionInput | null,
  groups: OrderThanksPrintGroup[],
): GiftThanksPrintGroupDto[] =>
  groups.flatMap((g) => {
    const dto = buildThanksPrintDto(selection, g.config, g.brandId, g.allowedTemplateIds);
    return dto ? [{ ...dto, giftItems: g.itemIds }] : [];
  });

/**
 * 장바구니 주문 DTO의 감사카드 부분.
 * - 카드 1장: **기존과 똑같이 thanksPrint 하나만** 싣는다 (thanksPrints 없음 → 서버도 기존 경로)
 *   단, 같은 브랜드에 카드를 못 받는 상품이 섞였으면(hasUncoveredItems) thanksPrints 로 — 1장 규칙은 그 상품까지
 *   대조해 400 이 난다. 이 경우 예전엔 카드 없이 결제됐고, 구서버에선 지금도 그렇게 된다.
 * - 카드 여러 장: thanksPrints 만. 대표(thanksPrint)를 싣지 않는 이유 — thanksPrints 를 모르는 구서버는
 *   thanksPrint 하나를 같은 브랜드 모든 상품에 대조해 400(= 결제 불가)을 내므로, 구서버에선 예전처럼
 *   카드 없이 나가게 한다. 새 서버는 첫 장으로 thanksPrint 를 다시 채운다.
 */
export const buildOrderThanksPrintPayload = (
  selection: ThanksPrintSelectionInput | null,
  groups: OrderThanksPrintGroup[],
): { thanksPrint?: GiftThanksPrintDto; thanksPrints?: GiftThanksPrintGroupDto[] } => {
  if (groups.length === 1 && groups[0] && !groups[0].hasUncoveredItems) {
    const g = groups[0];
    const thanksPrint = buildThanksPrintDto(selection, g.config, g.brandId, g.allowedTemplateIds);
    return thanksPrint ? { thanksPrint } : {};
  }
  const thanksPrints = buildThanksPrintGroupDtos(selection, groups);
  return thanksPrints.length ? { thanksPrints } : {};
};

/** 유효한 발신표현인지 */
export const isThanksSenderSuffix = (value: unknown): value is ThanksSenderSuffix =>
  THANKS_SENDER_SUFFIXES.includes(value as ThanksSenderSuffix);

/**
 * 저장된 발신자 값을 이름/표현으로 분리·정규화.
 * - suffix가 유효하지 않으면 기본 '배상'
 * - 레거시 합성값("홍길동 배상")처럼 이름에 표현이 붙어있으면 떼어냄
 *   (undefined가 붙는 "홍길동 배상 undefined" 류 깨짐 방지)
 */
export const parseThanksSender = (
  storedName: string | undefined,
  storedSuffix: unknown,
): { name: string; suffix: ThanksSenderSuffix } => {
  let name = (storedName ?? '').trim();
  let suffix = isThanksSenderSuffix(storedSuffix) ? storedSuffix : undefined;
  for (const s of THANKS_SENDER_SUFFIXES) {
    if (name.endsWith(` ${s}`)) {
      name = name.slice(0, name.length - s.length).trim();
      suffix = suffix ?? s;
      break;
    }
  }
  return { name: name || THANKS_FALLBACK_NAME, suffix: suffix ?? '배상' };
};

/** 감사글 입력 제한 */
export const THANKS_MESSAGE_MAX_LINES = 4;
export const THANKS_MESSAGE_MAX_CHARS = 100; // 줄바꿈 제외 글자수

/** 줄바꿈 제외 글자수 */
export const thanksMessageCharCount = (value: string): number =>
  value.replace(/\n/g, '').length;

/**
 * 감사글을 4줄 / 100자(줄바꿈 제외) 이내로 제한.
 * 초과분은 잘라내되 줄바꿈 위치는 보존.
 */
export const clampThanksMessage = (value: string): string => {
  const lines = value.split('\n').slice(0, THANKS_MESSAGE_MAX_LINES);
  let count = 0;
  let result = '';
  for (const ch of lines.join('\n')) {
    if (ch === '\n') {
      result += ch;
      continue;
    }
    if (count >= THANKS_MESSAGE_MAX_CHARS) break;
    result += ch;
    count += 1;
  }
  return result;
};

/**
 * 감사글 프리셋 (### 는 사용 시 member 표현으로 치환).
 * 줄바꿈은 인쇄 레이아웃에 맞춰 유지됨(break-keep + pre-line).
 */
export const THANKS_MESSAGE_PRESETS: string[] = [
  `바쁘신 와중에도 불구하고\n${THANKS_DECEASED_TOKEN}에\n위로해주셔서 감사합니다.`,
  `바쁘신 와중에도 친히\n${THANKS_DECEASED_TOKEN}에\n위로해 주셔서 감사합니다.`,
  `바쁘신 와중에도\n${THANKS_DECEASED_TOKEN}에\n귀한 발걸음 해 주신 덕분에\n큰 힘이 되었습니다.`,
  `조문과 부의로 위로해 주신 것에\n깊은 감사의 말씀을 드립니다.`,
  `${THANKS_DECEASED_TOKEN}에 따뜻한 마음으로\n위로해 주셔서 진심으로 감사합니다.\n덕분에 슬픔을 이겨낼 수 있었습니다.`,
];
